import {createContext, useCallback, useContext, useEffect, useMemo, useState} from "react";
import {useAuthContext} from "./AuthContext";

// И1: светла/тъмна тема.
//
// ПРАВИЛО (решение на възложителя, 01.08.2026): преди вписване всичко е СВЕТЛО.
// Тъмната тема е удобство за човека, който работи по цял ден в системата — не е
// нещо, което да се показва на случаен посетител. Landing, екранът за вписване и
// публичната страница по токен затова са светли винаги, без изключения по
// страници.
//
// Атрибутът data-bs-theme на <html> се пише САМО тук. Първата версия го пишеше
// на две места (провайдърът и една кука по страници) и родителският ефект
// печелеше, защото React изпълнява ефектите отдолу нагоре — Landing излизаше
// тъмна въпреки че беше поискала светла. Един писач, едно изчислено състояние.

const STORAGE_KEY = 'theme';

const readSavedTheme = () => {
    try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved === 'dark' || saved === 'light') {
            return saved;
        }
        // Първо влизане — следваме настройката на операционната система.
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    } catch (e) {
        // Частен режим / забранен localStorage — оставаме светли.
        return 'light';
    }
};

export const ThemeContext = createContext();

export const ThemeProvider = ({children}) => {
    const {isAuthenticated} = useAuthContext();
    const [theme, setTheme] = useState(readSavedTheme);

    // Брояч, а не флаг: ако някога два вложени екрана поискат светло, излизането
    // от единия не бива да отменя искането на другия.
    const [forcedLight, setForcedLight] = useState(0);

    const effective = (!isAuthenticated || forcedLight > 0) ? 'light' : theme;

    useEffect(() => {
        document.documentElement.setAttribute('data-bs-theme', effective);
    }, [effective]);

    // Изборът се пази независимо от това дали в момента важи — за да го намерим
    // такъв, какъвто е бил, при следващото вписване.
    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, theme);
        } catch (e) {
            // Темата ще важи само за текущата сесия — не е причина да се чупи нещо.
        }
    }, [theme]);

    const toggleTheme = useCallback(() => {
        setTheme(prev => (prev === 'dark' ? 'light' : 'dark'));
    }, []);

    // Връща функция за отписване — ползва се като cleanup на useEffect.
    const registerForcedLight = useCallback(() => {
        setForcedLight(n => n + 1);
        return () => setForcedLight(n => n - 1);
    }, []);

    const value = useMemo(() => ({
        theme,
        isDark: effective === 'dark',
        toggleTheme,
        registerForcedLight,
    }), [theme, effective, toggleTheme, registerForcedLight]);

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => {
    return useContext(ThemeContext);
};

// За екрани, които трябва да са светли ДОРИ на вписан потребител: правните
// страници (собственият им CSS е бял документ) и публичната страница по токен
// (показва сканирани документи на бял лист). Невписаният и без това е светъл.
export const useForceLightTheme = () => {
    const {registerForcedLight} = useTheme();

    useEffect(() => registerForcedLight(), [registerForcedLight]);
};
